import { useState, useCallback } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

export const useMeetings = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const assignBot = useCallback(async (meetingId, data) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/meetings/${meetingId}/assign-bot`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const engageBot = useCallback(async (meetingId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/meetings/${meetingId}/engage`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const disengageBot = useCallback(async (meetingId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/meetings/${meetingId}/disengage`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const startBot = useCallback(async (meetingId, trigger = 'manual') => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/meetings/${meetingId}/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ trigger })
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const stopBot = useCallback(async (meetingId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/meetings/${meetingId}/stop`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const instantMeeting = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/meetings/instant`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveMeetings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/meetings/active`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const result = await res.json();
      if (result.success) return result.data;
      return [];
    } catch (err) {
      console.error("Failed to fetch active meetings", err);
      return [];
    }
  }, []);

  const stopInstantMeeting = useCallback(async (meetingId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/meetings/${meetingId}/disengage`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const checkBulkEngagement = useCallback(async (eventIds) => {
      try {
          const res = await fetch(`${API_BASE}/api/meetings/bulk-check`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({ event_ids: eventIds })
          });
          const result = await res.json();
          if (result.success) return result.data;
          return {};
      } catch (err) {
          console.error("Bulk check failed", err);
          return {};
      }
  }, []);

  return {
    loading,
    error,
    assignBot,
    engageBot,
    disengageBot,
    startBot,
    stopBot,
    instantMeeting,
    fetchActiveMeetings,
    stopInstantMeeting,
    checkBulkEngagement
  };
};
